/**
 * Alshallal Pastry Company HR System - Company Documents Page
 * Phase 19: Company Documents + Expiry Tracking
 * Phase 25.1: Enhanced with Document Preview component
 * HR-only page for managing company-wide documents
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { format, differenceInDays } from 'date-fns';
import {
  Upload,
  FileText,
  Eye,
  Download,
  Clock,
  CheckCircle,
  RefreshCw,
  Plus,
  AlertCircle,
  Loader2,
  Calendar,
  File,
  Building2,
  Archive,
  RotateCcw,
  Filter,
  Search,
  FileSearch
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import DocumentPreview from '@/components/documents/DocumentPreview';
import { cn } from '@/lib/utils';
import OCRProcessingDialog from '@/components/documents/OCRProcessingDialog';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Category labels
const CATEGORY_LABELS = {
  company_policy: { en: 'Company Policy', ar: 'سياسة الشركة' },
  company_license: { en: 'Company License', ar: 'رخصة الشركة' },
  commercial_registration: { en: 'Commercial Registration', ar: 'السجل التجاري' },
  tax_certificate: { en: 'Tax Certificate', ar: 'الشهادة الضريبية' },
  other: { en: 'Other', ar: 'أخرى' },
};

// Urgency tier colors for expiry
const getExpiryBadgeStyle = (daysLeft) => {
  if (daysLeft === null || daysLeft === undefined) return null;
  if (daysLeft < 0) return 'bg-red-100 text-red-800 border-red-200';
  if (daysLeft <= 7) return 'bg-orange-100 text-orange-800 border-orange-200';
  if (daysLeft <= 30) return 'bg-amber-100 text-amber-800 border-amber-200';
  return 'bg-green-100 text-green-800 border-green-200';
};

const getExpiryLabel = (daysLeft, isRTL) => {
  if (daysLeft === null || daysLeft === undefined) return null;
  if (daysLeft < 0) return isRTL ? `منتهية منذ ${Math.abs(daysLeft)} يوم` : `Expired ${Math.abs(daysLeft)} days ago`;
  if (daysLeft === 0) return isRTL ? 'تنتهي اليوم' : 'Expires today';
  return isRTL ? `${daysLeft} يوم متبقي` : `${daysLeft} days left`;
};

const CompanyDocumentsPage = () => {
  const { token, user } = useAuth();
  const { t, language, isRTL } = useLanguage();
  const fileInputRef = useRef(null);

  const [documents, setDocuments] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filters
  const [categoryFilter, setCategoryFilter] = useState('');
  const [includeArchived, setIncludeArchived] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  // Upload dialog
  const [uploadDialog, setUploadDialog] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadForm, setUploadForm] = useState({
    category: '',
    display_name: '',
    issue_date: '',
    has_expiry: false,  // NEW: checkbox state
    expiry_date: '',
    notes: '',
  });
  const [selectedFile, setSelectedFile] = useState(null);

  // Archive dialog
  const [archiveDialog, setArchiveDialog] = useState(false);
  const [archiveTarget, setArchiveTarget] = useState(null);
  const [archiveReason, setArchiveReason] = useState('');
  const [archiving, setArchiving] = useState(false);

  // OCR Dialog state (Phase 24)
  const [ocrDialog, setOcrDialog] = useState({ open: false, document: null });

  // Preview Dialog state (Phase 25.1)
  const [previewDialog, setPreviewDialog] = useState({ open: false, document: null });

  // Fetch categories
  const fetchCategories = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/company-documents/categories`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCategories(response.data.categories || []);
    } catch (err) {
      console.error('Failed to fetch categories:', err);
    }
  }, [token]);

  // Fetch documents
  const fetchDocuments = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (categoryFilter) params.append('category', categoryFilter);
      if (includeArchived) params.append('include_archived', 'true');
      params.append('page', currentPage);
      params.append('page_size', 20);

      const response = await axios.get(`${API_URL}/company-documents/?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      let docs = response.data.documents || [];
      
      // Filter by search query locally
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        docs = docs.filter(doc => 
          (doc.display_name || '').toLowerCase().includes(query) ||
          (doc.file_name || '').toLowerCase().includes(query) ||
          (doc.category || '').toLowerCase().includes(query)
        );
      }
      
      setDocuments(docs);
      setTotalPages(response.data.total_pages || 1);
      setTotal(response.data.total || 0);
    } catch (err) {
      console.error('Failed to fetch documents:', err);
      setError(isRTL ? 'فشل في تحميل المستندات' : 'Failed to load documents');
    } finally {
      setLoading(false);
    }
  }, [token, categoryFilter, includeArchived, currentPage, searchQuery, isRTL]);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  // Handle file selection
  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  // Handle upload
  const handleUpload = async () => {
    if (!selectedFile || !uploadForm.category) {
      return;
    }

    // Validate display_name required for "other" category
    if (uploadForm.category === 'other' && !uploadForm.display_name.trim()) {
      setError(isRTL ? 'اسم العرض مطلوب لفئة "أخرى"' : 'Display name is required for "Other" category');
      return;
    }

    setUploading(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append('file', selectedFile);

      const params = new URLSearchParams();
      params.append('category', uploadForm.category);
      if (uploadForm.display_name) params.append('display_name', uploadForm.display_name);
      if (uploadForm.issue_date) params.append('issue_date', uploadForm.issue_date);
      // Only send expiry_date if checkbox is checked
      if (uploadForm.has_expiry && uploadForm.expiry_date) params.append('expiry_date', uploadForm.expiry_date);
      if (uploadForm.notes) params.append('notes', uploadForm.notes);

      const response = await axios.post(`${API_URL}/company-documents/?${params.toString()}`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      // Get uploaded document info
      const uploadedDoc = response.data;

      setUploadDialog(false);
      setUploadForm({ category: '', display_name: '', issue_date: '', has_expiry: false, expiry_date: '', notes: '' });
      setSelectedFile(null);
      await fetchDocuments();

      // Phase 24: Show OCR prompt after successful upload
      if (uploadedDoc?.id || uploadedDoc?.document?.id) {
        const docId = uploadedDoc.id || uploadedDoc.document?.id;
        setOcrDialog({ open: true, document: { id: docId, ...uploadedDoc } });
      }
    } catch (err) {
      console.error('Upload failed:', err);
      setError(err.response?.data?.detail?.error || (isRTL ? 'فشل في رفع المستند' : 'Failed to upload document'));
    } finally {
      setUploading(false);
    }
  };

  // Handle download
  const handleDownload = async (doc) => {
    try {
      const response = await axios.get(`${API_URL}/company-documents/${doc.id}/download`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', doc.file_name || 'document');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error('Download failed:', err);
      setError(isRTL ? 'فشل في تحميل المستند' : 'Failed to download document');
    }
  };

  // Handle archive
  const handleArchive = async () => {
    if (!archiveTarget || archiveReason.length < 10) return;

    setArchiving(true);
    try {
      await axios.post(
        `${API_URL}/company-documents/${archiveTarget.id}/archive`,
        { reason: archiveReason },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setArchiveDialog(false);
      setArchiveTarget(null);
      setArchiveReason('');
      fetchDocuments();
    } catch (err) {
      console.error('Archive failed:', err);
      setError(err.response?.data?.detail?.error || (isRTL ? 'فشل في أرشفة المستند' : 'Failed to archive document'));
    } finally {
      setArchiving(false);
    }
  };

  // Handle unarchive
  const handleUnarchive = async (doc) => {
    try {
      await axios.post(
        `${API_URL}/company-documents/${doc.id}/unarchive`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchDocuments();
    } catch (err) {
      console.error('Unarchive failed:', err);
      setError(err.response?.data?.detail?.error || (isRTL ? 'فشل في استعادة المستند' : 'Failed to restore document'));
    }
  };

  // Get category label
  const getCategoryLabel = (category) => {
    const cat = CATEGORY_LABELS[category];
    return cat ? (isRTL ? cat.ar : cat.en) : category;
  };

  // Check if user can archive (HR Admin or Super Admin only)
  const canArchive = user?.role === 'hr_admin' || user?.role === 'super_admin';

  return (
    <div className="p-6 space-y-6" data-testid="company-documents-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="flex items-center gap-3">
          <Building2 className="h-8 w-8 text-primary" />
          <div>
            <h1 className="text-2xl font-bold">
              {isRTL ? 'مستندات الشركة' : 'Company Documents'}
            </h1>
            <p className="text-sm text-muted-foreground">
              {isRTL ? 'إدارة المستندات على مستوى الشركة' : 'Manage company-wide documents'}
            </p>
          </div>
        </div>
        <Button onClick={() => setUploadDialog(true)} data-testid="upload-company-doc-btn">
          <Plus className="h-4 w-4 mr-2" />
          {isRTL ? 'رفع مستند' : 'Upload Document'}
        </Button>
      </div>

      {/* Error display */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center gap-2">
          <AlertCircle className="h-5 w-5" />
          {error}
        </div>
      )}

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Search */}
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder={isRTL ? 'البحث في المستندات...' : 'Search documents...'}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                  data-testid="search-input"
                />
              </div>
            </div>

            {/* Category filter */}
            <Select value={categoryFilter} onValueChange={(v) => setCategoryFilter(v === 'all' ? '' : v)}>
              <SelectTrigger className="w-[200px]" data-testid="category-filter">
                <Filter className="h-4 w-4 mr-2" />
                <SelectValue placeholder={isRTL ? 'الفئة' : 'Category'} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{isRTL ? 'جميع الفئات' : 'All Categories'}</SelectItem>
                {categories.filter(c => ['company_policy', 'company_license', 'commercial_registration', 'tax_certificate', 'other'].includes(c.key)).map(cat => (
                  <SelectItem key={cat.key} value={cat.key}>
                    {isRTL ? cat.label_ar : cat.label_en}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Include archived checkbox */}
            <div className="flex items-center gap-2">
              <Checkbox
                id="include-archived"
                checked={includeArchived}
                onCheckedChange={setIncludeArchived}
                data-testid="include-archived-checkbox"
              />
              <Label htmlFor="include-archived" className="text-sm">
                {isRTL ? 'عرض المؤرشفة' : 'Show Archived'}
              </Label>
            </div>

            {/* Refresh */}
            <Button variant="outline" onClick={fetchDocuments} disabled={loading}>
              <RefreshCw className={cn("h-4 w-4 mr-2", loading && "animate-spin")} />
              {isRTL ? 'تحديث' : 'Refresh'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Documents table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            {isRTL ? `المستندات (${total})` : `Documents (${total})`}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center items-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : documents.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Building2 className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>{isRTL ? 'لا توجد مستندات' : 'No documents found'}</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{isRTL ? 'المستند' : 'Document'}</TableHead>
                  <TableHead>{isRTL ? 'الفئة' : 'Category'}</TableHead>
                  <TableHead>{isRTL ? 'تاريخ الرفع' : 'Uploaded'}</TableHead>
                  <TableHead>{isRTL ? 'تاريخ الانتهاء' : 'Expiry'}</TableHead>
                  <TableHead>{isRTL ? 'الحالة' : 'Status'}</TableHead>
                  <TableHead className="text-right">{isRTL ? 'الإجراءات' : 'Actions'}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {documents.map(doc => (
                  <TableRow key={doc.id} className={doc.is_archived ? 'opacity-60' : ''}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <File className="h-5 w-5 text-muted-foreground" />
                        <div>
                          <div className="font-medium">{doc.display_name || doc.file_name}</div>
                          <div className="text-sm text-muted-foreground">{doc.file_name}</div>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">
                        {getCategoryLabel(doc.category)}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {doc.uploaded_at ? format(new Date(doc.uploaded_at), 'MMM d, yyyy') : '—'}
                    </TableCell>
                    <TableCell>
                      {doc.expiry_date ? (
                        <div className="space-y-1">
                          <div>{format(new Date(doc.expiry_date), 'MMM d, yyyy')}</div>
                          {doc.days_until_expiry !== null && (
                            <Badge className={cn("text-xs", getExpiryBadgeStyle(doc.days_until_expiry))}>
                              {getExpiryLabel(doc.days_until_expiry, isRTL)}
                            </Badge>
                          )}
                        </div>
                      ) : doc.requires_expiry ? (
                        <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200 text-xs gap-1" data-testid={`no-expiry-badge-${doc.id}`}>
                          <AlertCircle className="h-3 w-3" />
                          {isRTL ? 'لا يوجد تاريخ انتهاء' : 'No expiry set'}
                        </Badge>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {doc.is_archived ? (
                        <Badge variant="secondary">
                          <Archive className="h-3 w-3 mr-1" />
                          {isRTL ? 'مؤرشف' : 'Archived'}
                        </Badge>
                      ) : (
                        <Badge className="bg-green-100 text-green-800">
                          <CheckCircle className="h-3 w-3 mr-1" />
                          {isRTL ? 'نشط' : 'Active'}
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        {/* Phase 25.1: Preview button */}
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setPreviewDialog({ open: true, document: doc })}
                          title={isRTL ? 'معاينة' : 'Preview'}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleDownload(doc)}
                          title={isRTL ? 'تحميل' : 'Download'}
                        >
                          <Download className="h-4 w-4" />
                        </Button>
                        {/* Phase 24: Apply OCR button */}
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                          onClick={() => setOcrDialog({ open: true, document: doc })}
                          title={isRTL ? 'تطبيق OCR' : 'Apply OCR'}
                        >
                          <FileSearch className="h-4 w-4" />
                        </Button>
                        {canArchive && (
                          doc.is_archived ? (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleUnarchive(doc)}
                              title={isRTL ? 'استعادة' : 'Restore'}
                            >
                              <RotateCcw className="h-4 w-4" />
                            </Button>
                          ) : (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => {
                                setArchiveTarget(doc);
                                setArchiveDialog(true);
                              }}
                              title={isRTL ? 'أرشفة' : 'Archive'}
                            >
                              <Archive className="h-4 w-4" />
                            </Button>
                          )
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex justify-center gap-2 mt-4">
              <Button
                variant="outline"
                size="sm"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(p => p - 1)}
              >
                {isRTL ? 'السابق' : 'Previous'}
              </Button>
              <span className="flex items-center px-4 text-sm">
                {isRTL ? `صفحة ${currentPage} من ${totalPages}` : `Page ${currentPage} of ${totalPages}`}
              </span>
              <Button
                variant="outline"
                size="sm"
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage(p => p + 1)}
              >
                {isRTL ? 'التالي' : 'Next'}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Upload Dialog */}
      <Dialog open={uploadDialog} onOpenChange={setUploadDialog}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>{isRTL ? 'رفع مستند شركة' : 'Upload Company Document'}</DialogTitle>
            <DialogDescription>
              {isRTL ? 'قم برفع مستند جديد على مستوى الشركة' : 'Upload a new company-wide document'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Category */}
            <div className="space-y-2">
              <Label>{isRTL ? 'الفئة' : 'Category'} *</Label>
              <Select
                value={uploadForm.category}
                onValueChange={(v) => setUploadForm(prev => ({ ...prev, category: v }))}
              >
                <SelectTrigger data-testid="upload-category-select">
                  <SelectValue placeholder={isRTL ? 'اختر الفئة' : 'Select category'} />
                </SelectTrigger>
                <SelectContent>
                  {categories.filter(c => ['company_policy', 'company_license', 'commercial_registration', 'tax_certificate', 'other'].includes(c.key)).map(cat => (
                    <SelectItem key={cat.key} value={cat.key}>
                      {isRTL ? cat.label_ar : cat.label_en}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Display name (required for "other") */}
            <div className="space-y-2">
              <Label>
                {isRTL ? 'اسم العرض' : 'Display Name'}
                {uploadForm.category === 'other' && ' *'}
              </Label>
              <Input
                value={uploadForm.display_name}
                onChange={(e) => setUploadForm(prev => ({ ...prev, display_name: e.target.value }))}
                placeholder={isRTL ? 'أدخل اسم المستند' : 'Enter document name'}
                data-testid="upload-display-name"
              />
              {uploadForm.category === 'other' && (
                <p className="text-xs text-muted-foreground">
                  {isRTL ? 'مطلوب لفئة "أخرى"' : 'Required for "Other" category'}
                </p>
              )}
            </div>

            {/* File upload */}
            <div className="space-y-2">
              <Label>{isRTL ? 'الملف' : 'File'} *</Label>
              <div className="flex items-center gap-2">
                <Input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileSelect}
                  accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                  className="flex-1"
                  data-testid="upload-file-input"
                />
              </div>
              {selectedFile && (
                <p className="text-sm text-muted-foreground">
                  {selectedFile.name} ({(selectedFile.size / 1024).toFixed(1)} KB)
                </p>
              )}
            </div>

            {/* Issue date */}
            <div className="space-y-2">
              <Label>{isRTL ? 'تاريخ الإصدار' : 'Issue Date'}</Label>
              <Input
                type="date"
                value={uploadForm.issue_date}
                onChange={(e) => setUploadForm(prev => ({ ...prev, issue_date: e.target.value }))}
                data-testid="upload-issue-date"
              />
            </div>

            {/* Expiry Date Checkbox + Conditional Field */}
            <div className="space-y-3">
              <div className="flex items-center space-x-2 rtl:space-x-reverse">
                <Checkbox
                  id="has-expiry"
                  checked={uploadForm.has_expiry}
                  onCheckedChange={(checked) => setUploadForm(prev => ({ 
                    ...prev, 
                    has_expiry: checked,
                    expiry_date: checked ? prev.expiry_date : ''  // Clear date if unchecked
                  }))}
                  data-testid="has-expiry-checkbox"
                />
                <Label 
                  htmlFor="has-expiry" 
                  className="text-sm font-medium cursor-pointer"
                >
                  {isRTL ? 'هذا المستند له تاريخ انتهاء' : 'This document has an expiry date'}
                </Label>
              </div>
              
              {uploadForm.has_expiry && (
                <div className="space-y-2 pl-6 rtl:pl-0 rtl:pr-6">
                  <Label>{isRTL ? 'تاريخ الانتهاء' : 'Expiry Date'} *</Label>
                  <Input
                    type="date"
                    value={uploadForm.expiry_date}
                    onChange={(e) => setUploadForm(prev => ({ ...prev, expiry_date: e.target.value }))}
                    data-testid="upload-expiry-date"
                    required
                  />
                  <p className="text-xs text-muted-foreground">
                    {isRTL 
                      ? 'سيتم تتبع هذا المستند في تنبيهات انتهاء الصلاحية'
                      : 'This document will be tracked in expiry alerts'}
                  </p>
                </div>
              )}
            </div>

            {/* Notes */}
            <div className="space-y-2">
              <Label>{isRTL ? 'ملاحظات' : 'Notes'}</Label>
              <Textarea
                value={uploadForm.notes}
                onChange={(e) => setUploadForm(prev => ({ ...prev, notes: e.target.value }))}
                placeholder={isRTL ? 'ملاحظات إضافية...' : 'Additional notes...'}
                rows={3}
                data-testid="upload-notes"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setUploadDialog(false)}>
              {isRTL ? 'إلغاء' : 'Cancel'}
            </Button>
            <Button
              onClick={handleUpload}
              disabled={
                uploading || 
                !selectedFile || 
                !uploadForm.category || 
                (uploadForm.category === 'other' && !uploadForm.display_name.trim()) ||
                (uploadForm.has_expiry && !uploadForm.expiry_date)  // Require expiry date if checkbox checked
              }
              data-testid="upload-submit-btn"
            >
              {uploading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  {isRTL ? 'جارٍ الرفع...' : 'Uploading...'}
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4 mr-2" />
                  {isRTL ? 'رفع' : 'Upload'}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Archive Dialog */}
      <Dialog open={archiveDialog} onOpenChange={setArchiveDialog}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>{isRTL ? 'أرشفة المستند' : 'Archive Document'}</DialogTitle>
            <DialogDescription>
              {isRTL 
                ? 'سيتم إخفاء هذا المستند من القائمة الافتراضية. يمكن استعادته لاحقاً.'
                : 'This document will be hidden from the default list. It can be restored later.'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>{isRTL ? 'سبب الأرشفة' : 'Archive Reason'} *</Label>
              <Textarea
                value={archiveReason}
                onChange={(e) => setArchiveReason(e.target.value)}
                placeholder={isRTL ? 'أدخل سبب الأرشفة (10 حروف على الأقل)' : 'Enter reason for archiving (min 10 characters)'}
                rows={3}
                data-testid="archive-reason"
              />
              {archiveReason.length > 0 && archiveReason.length < 10 && (
                <p className="text-xs text-red-500">
                  {isRTL ? `${10 - archiveReason.length} حروف متبقية` : `${10 - archiveReason.length} more characters required`}
                </p>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setArchiveDialog(false);
              setArchiveTarget(null);
              setArchiveReason('');
            }}>
              {isRTL ? 'إلغاء' : 'Cancel'}
            </Button>
            <Button
              variant="destructive"
              onClick={handleArchive}
              disabled={archiving || archiveReason.length < 10}
              data-testid="archive-submit-btn"
            >
              {archiving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  {isRTL ? 'جارٍ الأرشفة...' : 'Archiving...'}
                </>
              ) : (
                <>
                  <Archive className="h-4 w-4 mr-2" />
                  {isRTL ? 'أرشفة' : 'Archive'}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Phase 24: OCR Processing Dialog */}
      <OCRProcessingDialog
        open={ocrDialog.open}
        onOpenChange={(open) => {
          if (!open) {
            setOcrDialog({ open: false, document: null });
          }
        }}
        document={ocrDialog.document}
        token={token}
        isRTL={isRTL}
        onComplete={(acceptedFields) => {
          // Refresh documents after OCR completion
          fetchDocuments();
        }}
        onSkip={() => {
          setOcrDialog({ open: false, document: null });
        }}
      />

      {/* Phase 25.1: Document Preview */}
      <DocumentPreview
        open={previewDialog.open}
        onClose={() => setPreviewDialog({ open: false, document: null })}
        document={previewDialog.document}
        token={token}
        isRTL={isRTL}
        documentType="company"
      />
    </div>
  );
};

export default CompanyDocumentsPage;
